import { useState } from "react";
import { FaCheckCircle, FaBriefcase, FaChevronDown } from "react-icons/fa";

const experiences = [
  {
    role: "ICT Instructor",
    company: "HEART/NSTA Trust",
    period: "May 2025 – Present",
    location: "Jamaica",
    type: "Part-Time",
    bullets: [
      "Deliver ICT training aligned with national Level 2 and Level 3 standards.",
      "Apply competency-based TVET methodologies and differentiated instruction strategies.",
      "Design and administer assessments to ensure trainee competency and industry readiness.",
    ],
  },
  {
    role: "Graduate Work Experience Programme",
    company: "HEART/NSTA Trust",
    period: "May 2024 – Dec 2024",
    location: "Jamaica",
    type: "Contract",
    bullets: [
      "Managed trainee data using eSkill, Moodle, and SharePoint.",
      "Analyzed performance data in Excel and created digital surveys in Microsoft Forms.",
      "Supported training delivery and program administration.",
    ],
  },
  {
    role: "Web Developer Intern",
    company: "Niritech Digital Services",
    period: "May 2022 – Jul 2022",
    location: "Jamaica",
    type: "Internship",
    bullets: [
      "Developed government websites using Drupal and WordPress.",
      "Created data reports and visualizations using Tableau.",
      "Structured project workflows and task systems in ClickUp.",
    ],
  },
];

const typeColors = {
      "Part-Time": "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  "Contract":  "bg-violet-500/10 text-violet-400 border-violet-500/20",
  "Internship":"bg-amber-500/10 text-amber-400 border-amber-500/20",
};

export default function Experience() {
  const [open, setOpen] = useState(0); // first card open by default

  return (
    <section id="experience" className="bg-navy-900 section-padding">
      <div className="container-max">
        <div className="text-center mb-14">
          <span className="section-tag">My Journey</span>
          <h2 className="section-title">Work Experience</h2>
          <p className="section-sub">
            Professional roles that have shaped my skills as a developer and educator.
          </p>
        </div>

        {/* Timeline */}
        <div className="max-w-3xl mx-auto relative">

          {/* Vertical line */}
          <div className="absolute left-6 top-0 bottom-0 w-px bg-navy-700 hidden sm:block" />

          <div className="space-y-0">
            {experiences.map((exp, i) => {
              const isOpen = open === i;
              const isLast = i === experiences.length - 1;

              return (
                <div key={exp.role + exp.company} className="relative sm:pl-16">

                  {/* Timeline dot */}
                  <div className={`absolute left-3.5 top-6 w-5 h-5 rounded-full border-2 hidden sm:flex
                                   items-center justify-center z-10 transition-colors duration-300
                                   ${isOpen
                                     ? "bg-cyan-500 border-cyan-400 shadow-glow"
                                     : "bg-navy-800 border-navy-600"}`}>
                    <div className={`w-1.5 h-1.5 rounded-full transition-colors duration-300
                                     ${isOpen ? "bg-white" : "bg-slate-600"}`} />
                  </div>

                  {/* Connector line segment highlight */}
                  {!isLast && (
                    <div className={`absolute left-6 top-11 w-px hidden sm:block transition-colors duration-500
                                     ${isOpen ? "bg-cyan-500/40 h-full" : "bg-navy-700 h-full"}`} />
                  )}

                  {/* Card */}
                  <div className={`mb-6 rounded-2xl border transition-all duration-300 overflow-hidden
                                   ${isOpen
                                     ? "border-cyan-500/40 bg-navy-800 shadow-glow"
                                     : "border-navy-700 bg-navy-800/60 hover:border-navy-600"}`}>

                    {/* Clickable header */}
                    <button
                      onClick={() => setOpen(isOpen ? null : i)}
                      className="w-full text-left px-5 py-4 flex items-center justify-between gap-4"
                    >
                      <div className="flex items-center gap-4">
                        {/* Icon */}
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0
                                         transition-colors duration-300
                                         ${isOpen ? "bg-cyan-500/20" : "bg-navy-700"}`}>
                          <FaBriefcase size={16} className={isOpen ? "text-cyan-400" : "text-slate-500"} />
                        </div>

                        {/* Role + company */}
                        <div className="text-left">
                          <h3 className={`font-bold text-sm transition-colors duration-300
                                          ${isOpen ? "text-white" : "text-slate-300"}`}>
                            {exp.role}
                          </h3>
                          <p className={`text-xs mt-0.5 font-semibold transition-colors duration-300
                                         ${isOpen ? "text-cyan-400" : "text-slate-500"}`}>
                            {exp.company}
                          </p>
                        </div>
                      </div>

                      {/* Right side: badges + chevron */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`hidden md:inline-flex text-xs font-semibold px-2.5 py-1 rounded-full border
                                          ${typeColors[exp.type]}`}>
                          {exp.type}
                        </span>
                        <span className="text-xs text-slate-500 whitespace-nowrap hidden sm:block">
                          {exp.period}
                        </span>
                        <FaChevronDown
                          size={12}
                          className={`text-slate-500 transition-transform duration-300 ${isOpen ? "rotate-180 text-cyan-400" : ""}`}
                        />
                      </div>
                    </button>

                    {/* Period on mobile */}
                    <div className={`px-5 transition-all duration-300 ${isOpen ? "block" : "hidden"} sm:hidden`}>
                      <span className="text-xs text-slate-500">{exp.period} · {exp.location}</span>
                    </div>

                    {/* Expandable body */}
                    <div className={`transition-all duration-300 overflow-hidden
                                     ${isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}`}>
                      <div className="px-5 pb-5 pt-1 border-t border-navy-700 mt-2">
                        <div className="flex items-center gap-2 mb-3 pt-3">
                          <span className="text-xs text-slate-500">{exp.location}</span>
                          <span className="text-slate-700">·</span>
                          <span className="text-xs text-slate-500">{exp.period}</span>
                        </div>
                        <ul className="space-y-2.5">
                          {exp.bullets.map((b) => (
                            <li key={b} className="flex items-start gap-3 text-sm text-slate-400 leading-relaxed">
                              <FaCheckCircle className="text-cyan-500/60 mt-0.5 flex-shrink-0" size={13} />
                              {b}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                  </div>
                </div>
              );
            })}
          </div>

          {/* Career start cap */}
          <div className="hidden sm:flex absolute left-4 bottom-6 w-4 h-4 rounded-full
                          bg-navy-700 border-2 border-navy-600 items-center justify-center">
            <div className="w-1 h-1 rounded-full bg-slate-600" />
          </div>

        </div>
      </div>
    </section>
  );
}